import { SignageAway, SignageAwayCategory, SignageFloorPlan, SignageOccupancy, SignageOccupant } from '../data/signage-service';

// One row in a signage people list.
export interface SignageListedPerson {
  key: string;
  name: string;
  initials: string;
  detail: string;
  firstAider: boolean;
  fireWarden: boolean;
}

export const deskListKey = (deskId: number) => `desk-${deskId}`;

function listed(key: string, occupant: SignageOccupant, detail: string): SignageListedPerson {
  const initials = occupant.initials?.toUpperCase() ?? '';
  return {
    key,
    // INITIALS mode sends no name, so the initials stand in for it
    name: occupant.displayName ?? initials,
    initials,
    detail,
    firstAider: occupant.firstAider,
    fireWarden: occupant.fireWarden,
  };
}

function byNameThenDetail(a: SignageListedPerson, b: SignageListedPerson): number {
  return a.name.localeCompare(b.name) || a.detail.localeCompare(b.detail, undefined, { numeric: true });
}

// Everyone at a desk on this floor for the given half day.
export function inOfficePeople(
  occupancy: SignageOccupancy,
  floorPlan: SignageFloorPlan | null,
  floorLabel: string,
  period: 'AM' | 'PM',
): SignageListedPerson[] {
  const deskLabels = new Map<number, string>(floorPlan?.desks.map(d => [d.id, d.label]) ?? []);
  const people: SignageListedPerson[] = [];
  occupancy.desks.forEach(d => {
    const occupant = period === 'AM' ? d.am : d.pm;
    if (!occupant) return;
    const detail = [floorLabel, deskLabels.get(d.deskId)].filter(Boolean).join(' - ');
    people.push(listed(deskListKey(d.deskId), occupant, detail));
  });
  return people.sort(byNameThenDetail);
}

// Everyone away from this office in the given way for the given half day.
// Rows show why they're out or where they're working, then the team.
export function awayPeople(away: SignageAway, category: SignageAwayCategory, period: 'AM' | 'PM'): SignageListedPerson[] {
  const people: SignageListedPerson[] = [];
  away.people.forEach((entry, index) => {
    const half = period === 'AM' ? entry.am : entry.pm;
    if (half?.category !== category) return;
    const detail = [half.label, entry.teamName].filter(Boolean).join(' · ');
    people.push(listed(`away-${index}`, entry.person, detail));
  });
  return people.sort(byNameThenDetail);
}
